import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { format } from "date-fns";
import {
  Loader2,
  Search,
  RefreshCw,
  Download,
  MoreHorizontal,
  Check,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchTransactions } from "@/services/fetchTransactions";
import { Skeleton } from "@/components/ui/skeleton";

// Assuming this interface matches your API response structure
interface Transaction {
  _id: string;
  transactionId: string;
  amount: number;
  paymentMethod: string;
  status: string;
  mpesaDetails?: {
    phoneNumber: string;
  };
  company: {
    _id: string;
    name: string;
  };
  order: {
    _id: string;
    plateNumber: string;
    totalCost: number;
    status: string;
    paymentStatus: string;
  };
  createdAt: string;
  checkoutRequestID?: string;
}

function Transactions() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["transactions"],
    queryFn: async () => {
      const data = await fetchTransactions();
      return data
        .map((transaction: Transaction) => {
          return { ...transaction, company: transaction.company?.name };
        })
        .sort(
          (a: Transaction, b: Transaction) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
  });

  const transactions = data || [];

  const filteredTransactions = transactions.filter((transaction) => {
    const matchesSearch =
      transaction.transactionId
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      transaction.mpesaDetails?.phoneNumber?.includes(searchQuery) ||
      transaction.order?.plateNumber
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter
      ? transaction.status.toLowerCase() === statusFilter.toLowerCase()
      : true;

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredTransactions.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentTransactions = filteredTransactions.slice(startIndex, endIndex);

  const paginationItems = [];
  const maxPaginationItems = 5;

  if (totalPages <= maxPaginationItems) {
    for (let i = 1; i <= totalPages; i++) {
      paginationItems.push(i);
    }
  } else {
    paginationItems.push(1);

    if (currentPage > 3) {
      paginationItems.push("ellipsis-start");
    }

    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      paginationItems.push(i);
    }

    if (currentPage < totalPages - 2) {
      paginationItems.push("ellipsis-end");
    }

    if (totalPages > 1) {
      paginationItems.push(totalPages);
    }
  }

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const clearFilters = () => {
    setStatusFilter(null);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(parseInt(newSize));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatPhoneNumber = (phoneNumber: string | undefined) => {
    if (!phoneNumber) return "-";

    if (phoneNumber.length > 6) {
      const prefix = phoneNumber.substring(0, 3);
      const suffix = phoneNumber.substring(phoneNumber.length - 3);
      const maskedLength = phoneNumber.length - 6;
      return `${prefix}${"*".repeat(maskedLength)}${suffix}`;
    }

    return phoneNumber;
  };

  return (
    <div className="contaner w-full max-w-8xl mx-auto py-6">
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold">Transactions</CardTitle>
              <CardDescription className="mt-1">
                View and manage all your payment transactions
              </CardDescription>
            </div>
            {/* <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </div> */}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search transactions..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="flex gap-2 w-full sm:w-auto justify-end items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Show:</span>
                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-20 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {statusFilter && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="h-9"
                >
                  Clear Filters
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9">
                    {statusFilter
                      ? `Status: ${statusFilter}`
                      : "Filter by Status"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setStatusFilter(null)}
                    className="flex items-center justify-between"
                  >
                    All
                    {statusFilter === null && <Check className="h-4 w-4" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setStatusFilter("Completed")}
                    className="flex items-center justify-between"
                  >
                    Completed
                    {statusFilter === "Completed" && (
                      <Check className="h-4 w-4" />
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setStatusFilter("Pending")}
                    className="flex items-center justify-between"
                  >
                    Pending
                    {statusFilter === "Pending" && (
                      <Check className="h-4 w-4" />
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setStatusFilter("Failed")}
                    className="flex items-center justify-between"
                  >
                    Failed
                    {statusFilter === "Failed" && <Check className="h-4 w-4" />}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>

          {statusFilter && (
            <div className="mb-4 flex flex-wrap gap-2">
              <Badge
                variant="secondary"
                className="px-3 py-1 flex items-center gap-1"
              >
                Status: {statusFilter}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStatusFilter(null)}
                  className="h-4 w-4 p-0 ml-1 text-muted-foreground hover:text-foreground"
                >
                  ×
                </Button>
              </Badge>
            </div>
          )}

          {isLoading ? (
            [1, 2, 3, 4].map((item) => (
              <div key={item} className="flex items-center space-x-4 w-full my-8">
                <div className="space-y-2 w-full">
                  <Skeleton className="h-4 w-[100%]" />
                  <Skeleton className="h-4 w-[100%]" />
                </div>
              </div>
            ))
          ) : isError ? (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-8 text-center text-sm text-destructive">
              {error?.message || "Failed to load transactions"}
            </div>
          ) : (
            <>
              <div className="rounded-md border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vehicle</TableHead>

                      <TableHead>Amount</TableHead>
                      <TableHead>Payment Method</TableHead>
                      <TableHead>Phone Number</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      {/* <TableHead>Actions</TableHead> */}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          className="text-center py-6 text-muted-foreground"
                        >
                          No transactions found
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentTransactions.map((transaction) => (
                        <TableRow key={transaction._id}>
                          <TableCell>{transaction.order.plateNumber}</TableCell>
                          <TableCell className="font-medium">
                            KES {transaction.amount.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {transaction.paymentMethod}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {formatPhoneNumber(
                              transaction.mpesaDetails?.phoneNumber
                            )}
                          </TableCell>
                          <TableCell>
                            {format(
                              new Date(transaction.createdAt),
                              "MMM dd, yyyy"
                            )}
                            <div className="text-xs text-muted-foreground">
                              {format(
                                new Date(transaction.createdAt),
                                "hh:mm a"
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={`${getStatusColor(
                                transaction.status
                              )}`}
                            >
                              {transaction.status}
                            </Badge>
                          </TableCell>
                          {/* <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem>
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  Retry Payment
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  Cancel Transaction
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell> */}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {filteredTransactions.length > 0 && (
                <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-4">
                  {/* Results summary */}
                  <div className="text-sm text-muted-foreground w-full md:w-auto text-center md:text-left mb-3 md:mb-0">
                    Showing {startIndex + 1} to{" "}
                    {Math.min(endIndex, filteredTransactions.length)} of{" "}
                    {filteredTransactions.length} transactions
                  </div>

                  {/* Pagination */}
                  <div className="w-full md:w-auto flex justify-center md:justify-end">
                    <Pagination>
                      <PaginationContent className="flex-wrap justify-center">
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => handlePageChange(currentPage - 1)}
                            className={
                              currentPage === 1
                                ? "pointer-events-none opacity-50"
                                : "cursor-pointer hover:bg-accent transition-colors"
                            }
                            aria-disabled={currentPage === 1}
                          />
                        </PaginationItem>

                        {/* Hide some page numbers on small screens */}
                        {paginationItems.map((item, index) => {
                          if (
                            item === "ellipsis-start" ||
                            item === "ellipsis-end"
                          ) {
                            return (
                              <PaginationItem
                                key={`ellipsis-${index}`}
                                className="hidden sm:inline-flex"
                              >
                                <PaginationEllipsis />
                              </PaginationItem>
                            );
                          }

                          // On very small screens only show current page, first and last
                          if (
                            typeof item === "number" &&
                            item !== 1 &&
                            item !== totalPages &&
                            item !== currentPage &&
                            Math.abs(item - currentPage) === 1
                          ) {
                            return (
                              <PaginationItem
                                key={`page-${item}`}
                                className="hidden sm:inline-flex"
                              >
                                <PaginationLink
                                  isActive={currentPage === item}
                                  onClick={() =>
                                    handlePageChange(item as number)
                                  }
                                  className="hover:bg-accent transition-colors"
                                >
                                  {item}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          }

                          // Always show first, last and current page
                          if (typeof item === "number") {
                            return (
                              <PaginationItem key={`page-${item}`}>
                                <PaginationLink
                                  isActive={currentPage === item}
                                  onClick={() =>
                                    handlePageChange(item as number)
                                  }
                                  className="hover:bg-accent transition-colors"
                                >
                                  {item}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          }

                          return null;
                        })}

                        <PaginationItem>
                          <PaginationNext
                            onClick={() => handlePageChange(currentPage + 1)}
                            className={
                              currentPage === totalPages
                                ? "pointer-events-none opacity-50"
                                : "cursor-pointer hover:bg-accent transition-colors"
                            }
                            aria-disabled={currentPage === totalPages}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Transactions;
